import { createRef, useEffect, useMemo, useRef, useState } from "react";

import { SPACING } from "../../../constants/design/spacing";
import { Chevron } from "../Chevron/style";
import Dropdown from "../Dropdown";
import { DropdownItem } from "../DropdownSimple/style";
import {
  ActiveBackground,
  SegmentControlButton,
  SegmentControlButtonText,
  SegmentControlContainer,
  WidthType,
} from "./style";

export interface ISegmentedControlConfig {
  theme?: "outline";
  color?: string;
  backgroundColor?: string;
  activeBackgroundColor?: string;
  widthType?: WidthType;
  button?: {
    px?: number;
    py?: number;
    height?: number;
    fontSize: string;
  };
  borderRadius?: string;
  margin?: string;
}

export type ISegmentType = {
  type?: string;
  textColor?: string;
  disabled?: boolean;
} & (
  | {
      type?: "default";
      value: string;
      display?: string | JSX.Element;
    }
  | {
      type: "dropdown";
      value: string;
      display: string;
      items: {
        title: string;
        value: string;
        onSelectItem: () => void;
      }[];
    }
);

interface ISegmentControlProps {
  segments: ISegmentType[];
  value: string;
  onSelect?: (value: string) => void;
  config?: ISegmentedControlConfig;
  hasDropdown?: boolean;
}

function SegmentControl({
  segments,
  value,
  onSelect,
  config: {
    theme,
    color,
    backgroundColor,
    activeBackgroundColor,
    widthType = "maxContent",
    button = { px: 16, py: 12, fontSize: "14px" },
    borderRadius,
    margin,
  } = {},
  hasDropdown,
}: ISegmentControlProps) {
  const controlRefs = useMemo(
    () =>
      segments.reduce<any>((acc, curr) => {
        acc[curr.value] = createRef();
        return acc;
      }, {}),
    [segments]
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeBackgroundState, setActiveBackgroundState] = useState<
    object | boolean
  >(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = controlRefs[value]?.current;
    const container = containerRef.current;

    if (!currentRef) {
      return () => {};
    }

    const handleResize = () => {
      const rect = currentRef.getBoundingClientRect();
      setActiveBackgroundState({
        left: currentRef.offsetLeft,
        top: currentRef.offsetTop,
        height: rect.height,
        width: rect.width,
      });
    };
    handleResize();

    // Listen for window resize
    window.addEventListener("resize", handleResize, { passive: true });

    // Use ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    if (container) {
      resizeObserver.observe(container);
    }
    resizeObserver.observe(currentRef);

    // Use IntersectionObserver to quickly detect position changes
    const intersectionObserver = new IntersectionObserver(
      () => {
        handleResize();
      },
      { threshold: [0, 0.1, 0.5, 0.9, 1] }
    );

    intersectionObserver.observe(currentRef);

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [value, controlRefs, theme]);

  return (
    <SegmentControlContainer
      ref={containerRef}
      color={color}
      backgroundColor={backgroundColor}
      widthType={widthType}
      style={{ borderRadius, margin }}
    >
      {segments.map((segment) => {
        if (segment.type === "dropdown") {
          return (
            <SegmentControlButton
              key={""}
              role="button"
              ref={controlRefs[segment.value]}
              widthType={widthType}
              px={button.px}
              py={button.py}
              height={button.height}
              backgroundColor={
                theme === "outline" && value === segment.value
                  ? activeBackgroundColor
                  : "transparent"
              }
              disabled={segment.disabled}
              style={{ borderRadius }}
            >
              <Dropdown
                width={"100%"}
                isToggled={dropdownOpen}
                onToggle={(open) => setDropdownOpen(open)}
                dropdownMenuContainerStyles={{
                  width: 102,
                  outline: "none",
                  marginRight: button.px ? -button.px : undefined,
                }}
                toggleStyles={{
                  padding: 0,
                  height: button.height,
                  backgroundColor: "transparent",
                  border: "none",
                }}
                title={
                  <SegmentControlButtonText
                    color={segment.textColor ?? color}
                    style={{
                      fontSize: button.fontSize,
                    }}
                  >
                    {segment.display}
                    <Chevron
                      style={{ marginLeft: SPACING.one }}
                      direction={dropdownOpen ? "up" : "down"}
                    />
                  </SegmentControlButtonText>
                }
                items={segment.items.map(
                  ({ title, value: v, onSelectItem }) => ({
                    label: (
                      <DropdownItem
                        key={title}
                        isSelected={segment.value === v}
                      >
                        {title}
                      </DropdownItem>
                    ),
                    onSelect: () => {
                      onSelectItem();
                      setDropdownOpen(false);
                    },
                  })
                )}
              />
            </SegmentControlButton>
          );
        }
        return (
          <SegmentControlButton
            key={segment.value}
            role="button"
            onClick={(e) => {
              if (!hasDropdown) {
                e.stopPropagation();
              }
              if (!segment.disabled) {
                onSelect?.(segment.value);
                setDropdownOpen(false);
              }
            }}
            ref={controlRefs[segment.value]}
            widthType={widthType}
            px={button.px}
            py={button.py}
            height={button.height}
            backgroundColor={
              theme === "outline" && value === segment.value
                ? activeBackgroundColor
                : "transparent"
            }
            disabled={segment.disabled}
            style={{ borderRadius }}
          >
            <SegmentControlButtonText
              color={segment.textColor ?? color}
              style={{
                fontSize: button.fontSize,
              }}
            >
              {segment.display}
            </SegmentControlButtonText>
          </SegmentControlButton>
        );
      })}
      <ActiveBackground
        transition={{
          type: "keyframes",
          ease: "easeOut",
        }}
        initial={{
          height: "100%",
          width: `calc(100% / ${segments.length})`,
        }}
        animate={activeBackgroundState}
        theme={theme}
        color={theme === "outline" ? color : activeBackgroundColor}
        style={{ borderRadius }}
      />
    </SegmentControlContainer>
  );
}

export default SegmentControl;
